import { useMemo } from 'react';

import { DefaultError } from '@tanstack/react-query';

import { Workload } from '@cast/types';

import {
  UseNodeDependencies,
  useNodeDependencies,
  UseNodeDependenciesArgs,
} from 'features/cluster/nodes/hooks/useNodeDependencies';
import { NodeWithDependencies } from 'features/cluster/nodes/types';
import { useNodesQuery } from 'hooks/queries/nodes';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export type UseNodes = {
  nodes?: NodeWithDependencies[];
  workloads?: Workload[];
  isLoading: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  error?: DefaultError;
} & Omit<UseNodeDependencies, 'addNodeDependencies'>;

type UseNodesArgs = UseNodeDependenciesArgs & {
  /** Keep requesting node pages until exhausted. */
  fetchAllPages?: boolean;
  pageLimit?: number;
};

export const useNodes = ({
  clusterId: _clusterId,
  refetchInterval,
  fetchAllPages,
  pageLimit,
  ...nodeDependenciesArgs
}: UseNodesArgs = {}): UseNodes => {
  const { cluster } = useCluster();
  const clusterId = _clusterId || cluster.id;

  const {
    nodes: _nodes,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useNodesQuery({
    clusterId,
    refetchInterval,
    fetchAllPages,
    pageLimit,
  });

  const {
    refetch: refetchDependencies,
    addNodeDependencies,
    hasResultsWithError: hasDependenciesError,
    ...nodeDependencies
  } = useNodeDependencies({
    clusterId: _clusterId,
    refetchInterval,
    ...nodeDependenciesArgs,
  });

  const nodes = useMemo(() => {
    if (!_nodes) {
      return undefined;
    }
    if (!addNodeDependencies) {
      return _nodes;
    }
    return _nodes.map(addNodeDependencies);
  }, [_nodes, addNodeDependencies]);

  return {
    ...nodeDependencies,
    nodes,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: () => {
      void fetchNextPage();
    },
    error: error || undefined,
    hasResultsWithError: Boolean(error || hasDependenciesError),
    refetch: () => {
      refetch();
      refetchDependencies();
    },
  };
};
